import React, { useState } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import _ from 'lodash';
import AppLink from '../components/common/AppLink/AppLink';
import ListHeader from '../components/ListHeader';
import FilterSidebar from '../components/common/filters/FilterSidebar';
import { useSearchTerm } from '../hooks/useSearchTerm';
import { useSyncPageParams } from '../hooks/useSyncPageParams';
import { useGetAllTaskTemplate } from '../services/react-query/task_templates';
import { defaultContainerStyling, listPageLayoutStyles, listPageTableRowStyling, listPageTableWrapperStyles } from '../styles/sxStyling';
import { Loading } from '../uikit';
import { UserConfigurableModule } from '../types/User';
import { FilterState, IFilter } from '../components/common/filters/interfaces';
import { archiveFilter } from '../components/common/filters/consts';
import { ROUTE_PATHS } from '../routes/Routes';
import { useIsMobile } from '../hooks/useIsMobile';
import PaginationBar from '../components/PaginationBar';

export default function TaskTemplatesList() {
  const { currentPage, setCurrentPage } = useSyncPageParams();
  const [filterState, setFilterState] = useState<FilterState>(undefined);
  const { isMobile } = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

  const { data, isLoading } = useGetAllTaskTemplate({
    params: {
      page: currentPage,
      search: useSearchTerm(),
      filters: filterState,
    },
    enabled: true,
  });

  if (isLoading) return <Loading />;

  const taskTemplates = data?.data || [];
  const meta = data?.meta;
  const totalPages = Number(meta?.total_pages);
  const moduleFilters: IFilter[] = [archiveFilter];

  const tableRows = _.map(taskTemplates, (template) => (
    <TableRow key={template.uuid} sx={{ ...listPageTableRowStyling }}>
      <TableCell>
        <AppLink to={`/${ROUTE_PATHS.TASK_TEMPLATES}/${template.uuid}`}>{template.name}</AppLink>
      </TableCell>
      <TableCell>{template.description}</TableCell>
      <TableCell>{template.fields.length || 0}</TableCell>
      <TableCell>{template.steps.length || 0}</TableCell>
    </TableRow>
  ));

  return (
    <Box>
      <ListHeader
        title="Task Templates"
        filtersOpen={sidebarOpen}
        onToggleFilters={() => setSidebarOpen((o) => !o)}
      />

      <Box sx={listPageLayoutStyles}>
        {sidebarOpen && (
          <FilterSidebar
            moduleFilters={moduleFilters}
            module={UserConfigurableModule.TASK_TEMPLATES}
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            activeFilter={filterState}
            setActiveFilter={setFilterState}
          />
        )}

        <Box sx={listPageTableWrapperStyles}>
          <TableContainer sx={defaultContainerStyling}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Field Count</TableCell>
                  <TableCell>Step Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{tableRows}</TableBody>
            </Table>
          </TableContainer>
          {totalPages > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <PaginationBar page={currentPage} count={totalPages} onChange={(_e, v) => setCurrentPage(v)} />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
